/**
 * Fretboard — mapping pitches to physical locations on a fretted instrument.
 *
 * Pure, like the rest of theory-engine: no rendering, no DOM, no SVG. This is
 * the math a fretboard UI and the guitar-track exercise generators consume.
 *
 * The defining property is that pitch → fret is ONE-TO-MANY: a given MIDI
 * value sits at several positions across the neck. Recognition grading is
 * therefore set-membership ("is this tap one of the positions that produce
 * the target?"), not equality against a single answer.
 *
 * Conventions:
 *   - `stringIndex` 0 is the lowest-pitched (thickest) string; in standard
 *     tuning that is low E. This matches how `openStrings` is ordered.
 *   - `STRING_SETS` is indexed top-set-first (the pedagogical sequence —
 *     strings 1-2-3 first), which is the OPPOSITE direction from stringIndex.
 *     See the constant for the full mapping.
 *   - `P4_STRING_PAIRS` and `GB_STRING_PAIR` drive the rung-1 shape-family
 *     generator (PR-B). Each pair has a `lower` (root) and `upper` (interval)
 *     stringIndex; the generator places the root on `lower` and accepts the
 *     answer on `upper` only. See docs/design/guitar-harmony-v2.md §4.1.
 */

import type { Pitch, PitchClass } from "./pitch.js";
import { pitch, toMidi } from "./pitch.js";

export interface Tuning {
  /** Open-string pitches, lowest string (index 0) to highest. */
  openStrings: Pitch[];
  /** Highest playable fret. Fret 0 is the open string. */
  fretCount: number;
}

/** Standard 6-string guitar tuning: E2 A2 D3 G3 B3 E4. The only tuning in v1. */
export const STANDARD_TUNING: Tuning = {
  openStrings: [
    pitch("E", 0, 2), // 0 — low E
    pitch("A", 0, 2), // 1 — A
    pitch("D", 0, 3), // 2 — D
    pitch("G", 0, 3), // 3 — G
    pitch("B", 0, 3), // 4 — B
    pitch("E", 0, 4), // 5 — high e
  ],
  fretCount: 15,
};

/** A physical location on the neck. */
export interface FretPosition {
  /** 0 = lowest-pitched (thickest) string. */
  stringIndex: number;
  /** 0 = open string. */
  fret: number;
}

/**
 * The four close-voiced 3-string triad sets, ordered TOP-SET-FIRST to match
 * the pedagogical sequence (start on strings 1-2-3). Each entry lists the
 * `stringIndex` members of that set; member order is irrelevant to grading.
 *
 *   index 0 → strings 1-2-3 (high e, B, G) → stringIndex {3,4,5}
 *   index 1 → strings 2-3-4 (B, G, D)      → stringIndex {2,3,4}
 *   index 2 → strings 3-4-5 (G, D, A)      → stringIndex {1,2,3}
 *   index 3 → strings 4-5-6 (D, A, low E)  → stringIndex {0,1,2}
 *
 * Note the decoupling: a triad item's `stringSet` param indexes THIS array,
 * not raw stringIndex.
 */
export const STRING_SETS: readonly (readonly number[])[] = [
  [3, 4, 5],
  [2, 3, 4],
  [1, 2, 3],
  [0, 1, 2],
];

/** MIDI number sounding at a position. Throws if the string doesn't exist. */
export function midiAt(t: Tuning, p: FretPosition): number {
  const open = t.openStrings[p.stringIndex];
  if (open === undefined) {
    throw new Error(`midiAt: no string at index ${p.stringIndex}`);
  }
  return toMidi(open) + p.fret;
}

/** Pitch class (0-11) sounding at a position. */
export function pitchClassAt(t: Tuning, p: FretPosition): PitchClass {
  return (((midiAt(t, p) % 12) + 12) % 12) as PitchClass;
}

// ─── Shape-family tables (PR-B) ───────────────────────────────────────────────

/**
 * A two-string pair used as the unit of rung-1 shape-family training.
 * The root is placed on `lower`; the interval note lands on `upper`.
 */
export interface StringPair {
  lower: number;
  upper: number;
}

/**
 * The four perfect-fourth adjacent string pairs in standard tuning (E–A, A–D,
 * D–G, B–e). Shapes are identical across all four, so one FSRS item per
 * interval covers all of them; the generator randomises which pair each round.
 *
 * String indices (0 = low E): E=0, A=1, D=2, G=3, B=4, e=5.
 */
export const P4_STRING_PAIRS: readonly StringPair[] = [
  { lower: 0, upper: 1 }, // E–A
  { lower: 1, upper: 2 }, // A–D
  { lower: 2, upper: 3 }, // D–G
  { lower: 4, upper: 5 }, // B–e
];

/**
 * The G–B pair: tuned a major third (4 semitones) instead of a perfect fourth.
 * Every shape shifts +1 fret relative to the P4 pairs — trained as a separate
 * FSRS node so the physical offset is learned explicitly.
 */
export const GB_STRING_PAIR: StringPair = { lower: 3, upper: 4 };

/**
 * A string-skip octave shape — root on `lower`, octave on `upper` (one string
 * skipped between them), `fretOffset` = upper fret − root fret.
 */
export interface OctaveShape {
  lower: number;
  upper: number;
  /** upperFret = rootFret + fretOffset. */
  fretOffset: number;
  label: string;
}

/**
 * The four string-skip (skip-1-string) octave grips in standard tuning.
 *
 * | Shape | Strings (guitar notation) | fretOffset | Why |
 * |-------|--------------------------|------------|-----|
 * | E–D   | 6–4 (stringIndex 0–2)   | +2         | Two P4s = 10 semi, P8 = 12 → +2 |
 * | A–G   | 5–3 (stringIndex 1–3)   | +2         | Same geometry |
 * | D–B   | 4–2 (stringIndex 2–4)   | +3         | Crosses G–B gap: P4+M3 = 9 semi, P8 → +3 |
 * | G–e   | 3–1 (stringIndex 3–5)   | +3         | Same as D–B: M3+P4 = 9 semi, P8 → +3 |
 */
export const OCTAVE_SHAPES: readonly OctaveShape[] = [
  { lower: 0, upper: 2, fretOffset: 2, label: "E–D (6–4)" },
  { lower: 1, upper: 3, fretOffset: 2, label: "A–G (5–3)" },
  { lower: 2, upper: 4, fretOffset: 3, label: "D–B (4–2)" },
  { lower: 3, upper: 5, fretOffset: 3, label: "G–e (3–1)" },
];

/**
 * A skip-1-string interval shape: same string-pair geometry as the octave
 * shapes but a different interval (m7 or M6). fretOffset = 0 for the same-fret
 * cases trained first; the `interval` field identifies the sounding interval.
 */
export interface Skip1IntervalShape {
  lower: number;
  upper: number;
  /** The sounding interval above the root. */
  interval: "m7" | "M6";
  /** upperFret = rootFret + fretOffset (0 = same fret). */
  fretOffset: number;
  label: string;
}

/**
 * Four same-fret skip-1-string interval shapes.
 *
 * | Shape | Strings | Interval | fretOffset | Why |
 * |-------|---------|----------|------------|-----|
 * | E–D   | 6–4     | m7 (10)  | 0          | Open Δ = 10 semi, m7 = 10 → same fret |
 * | A–G   | 5–3     | m7 (10)  | 0          | Same geometry |
 * | D–B   | 4–2     | M6 (9)   | 0          | Open Δ = 9 semi (crosses G–B), M6 = 9 → same fret |
 * | G–e   | 3–1     | M6 (9)   | 0          | Same as D–B |
 */
export const SKIP1_INTERVAL_SHAPES: readonly Skip1IntervalShape[] = [
  { lower: 0, upper: 2, interval: "m7", fretOffset: 0, label: "m7 · E–D (6–4)" },
  { lower: 1, upper: 3, interval: "m7", fretOffset: 0, label: "m7 · A–G (5–3)" },
  { lower: 2, upper: 4, interval: "M6", fretOffset: 0, label: "M6 · D–B (4–2)" },
  { lower: 3, upper: 5, interval: "M6", fretOffset: 0, label: "M6 · G–e (3–1)" },
];

/**
 * Three wide-span (skip-2-string) octave grips — upper note sits at a LOWER
 * fret than the root because the open-string span exceeds 12 semitones.
 *
 * | Shape | Strings (guitar notation) | fretOffset | Open Δ |
 * |-------|--------------------------|------------|--------|
 * | E–G   | 6–3 (stringIndex 0–3)   | −3         | 15 semi (3×P4) |
 * | A–B   | 5–2 (stringIndex 1–4)   | −2         | 14 semi (2×P4 + M3) |
 * | D–e   | 4–1 (stringIndex 2–5)   | −2         | 14 semi (P4 + M3 + P4) |
 */
export const WIDE_OCTAVE_SHAPES: readonly OctaveShape[] = [
  { lower: 0, upper: 3, fretOffset: -3, label: "E–G (6–3)" },
  { lower: 1, upper: 4, fretOffset: -2, label: "A–B (5–2)" },
  { lower: 2, upper: 5, fretOffset: -2, label: "D–e (4–1)" },
];

// ─── Pitch lookup helpers ─────────────────────────────────────────────────────

/**
 * Every position that produces an exact MIDI value within the fret range.
 * This is the one-to-many lookup — a single pitch appears on several strings.
 * Used to build the accepted-answer set for the interval exercise, where the
 * octave matters (so it keys on MIDI, not pitch class).
 */
export function positionsForMidi(t: Tuning, midi: number): FretPosition[] {
  const out: FretPosition[] = [];
  for (let stringIndex = 0; stringIndex < t.openStrings.length; stringIndex++) {
    const fret = midi - toMidi(t.openStrings[stringIndex]!);
    if (fret >= 0 && fret <= t.fretCount) out.push({ stringIndex, fret });
  }
  return out;
}

/**
 * Every position of a pitch CLASS on the strings of a given set, in any
 * octave within the fret range. Used to build the accepted-answer set for
 * the triad/inversion exercises, where any octave of a chord tone on the set
 * is a valid voicing.
 *
 * `stringSet` is a list of `stringIndex` values (e.g. an entry of `STRING_SETS`).
 */
export function positionsOnStringSet(
  t: Tuning,
  pc: PitchClass,
  stringSet: readonly number[],
): FretPosition[] {
  const out: FretPosition[] = [];
  for (const stringIndex of stringSet) {
    const open = t.openStrings[stringIndex];
    if (open === undefined) continue;
    const openMidi = toMidi(open);
    for (let fret = 0; fret <= t.fretCount; fret++) {
      if (((openMidi + fret) % 12) === pc) out.push({ stringIndex, fret });
    }
  }
  return out;
}

// ─── CAGED shapes ─────────────────────────────────────────────────────────────

export type CagedShape = "C" | "A" | "G" | "E" | "D";

export interface CagedVoice {
  /** Semitone offset from the root pitch (major-quality reference value). */
  semitones: number;
  role: "root" | "third" | "fifth";
}

export interface CagedShapeDefinition {
  /** String index carrying the canonical root (the "identifying" anchor — see guitar-caged-v1.md §5.4). */
  canonicalRootString: number;
  /**
   * Chord-tone pattern keyed by string index.
   * `semitones` is the major-quality reference offset from the root pitch.
   * The generator adjusts the third for minor quality at runtime:
   * minor third = Math.floor(semitones / 12) * 12 + 3 (vs. +4 for major).
   * Only strings within the shape's playable span are listed.
   * Semitones may be negative for shapes where some voices sit below the
   * canonical root string in pitch (e.g. C-shape A/D/G strings below B root).
   */
  pattern: ReadonlyMap<number, CagedVoice>;
  /**
   * Minimum root fret for barre exercises. Defaults to 2.
   * Shapes whose open-chord root is at fret > 0 need a higher minimum
   * to avoid the open-chord position (G-shape and D-shape: 4; C-shape: 2).
   */
  minRootFret?: number;
}

/**
 * All five CAGED shape definitions.
 *
 * E-shape: root string 0 (low E). Classic 6-string barre.
 * A-shape: root string 1 (A string). 5-string barre.
 * G-shape: root string 0 (same as E). D/G/B/e strings are 3 frets below the root
 *   fret (the barre provides those notes); minRootFret=4 avoids the open G chord.
 * C-shape: root string 4 (B string). A/D/G voices are in the octave below the
 *   B-string root (negative semitone offsets); minRootFret=2.
 * D-shape: root string 4 (B string, same anchor as C). Only D/G/B/e strings active;
 *   D string carries an octave-below root; minRootFret=4 avoids open D.
 *   (The v1 design doc placeholder listed canonicalRootString=3 for D-shape; the
 *   correct anchor is string 4 — the B string carries the root in open D.)
 *
 * semitones are major-quality reference offsets. Negative values sit below the
 * root pitch (lower octave). The generator applies the minor third correction at
 * runtime: Math.floor(semitones/12)*12 + (maj?4:3).
 */
export const CAGED_SHAPES: Readonly<Partial<Record<CagedShape, CagedShapeDefinition>>> = {
  E: {
    canonicalRootString: 0,
    pattern: new Map<number, CagedVoice>([
      [0, { semitones: 0,  role: "root"  }],
      [1, { semitones: 7,  role: "fifth" }],
      [2, { semitones: 12, role: "root"  }],
      [3, { semitones: 16, role: "third" }],
      [4, { semitones: 19, role: "fifth" }],
      [5, { semitones: 24, role: "root"  }],
    ]),
  },
  A: {
    canonicalRootString: 1,
    pattern: new Map<number, CagedVoice>([
      [1, { semitones: 0,  role: "root"  }],
      [2, { semitones: 7,  role: "fifth" }],
      [3, { semitones: 12, role: "root"  }],
      [4, { semitones: 16, role: "third" }],
      [5, { semitones: 19, role: "fifth" }],
    ]),
  },
  G: {
    canonicalRootString: 0,
    minRootFret: 4,
    pattern: new Map<number, CagedVoice>([
      [0, { semitones: 0,  role: "root"  }],
      [1, { semitones: 4,  role: "third" }],
      [2, { semitones: 7,  role: "fifth" }],
      [3, { semitones: 12, role: "root"  }],
      [4, { semitones: 16, role: "third" }],
      [5, { semitones: 24, role: "root"  }],
    ]),
  },
  C: {
    canonicalRootString: 4,
    pattern: new Map<number, CagedVoice>([
      [1, { semitones: -12, role: "root"  }],
      [2, { semitones: -8,  role: "third" }],
      [3, { semitones: -5,  role: "fifth" }],
      [4, { semitones: 0,   role: "root"  }],
      [5, { semitones: 4,   role: "third" }],
    ]),
  },
  D: {
    canonicalRootString: 4,
    minRootFret: 4,
    pattern: new Map<number, CagedVoice>([
      [2, { semitones: -12, role: "root"  }],
      [3, { semitones: -5,  role: "fifth" }],
      [4, { semitones: 0,   role: "root"  }],
      [5, { semitones: 4,   role: "third" }],
    ]),
  },
};
